import React from 'react';
import classNames from 'classnames';
import { Block, NativeFormBlockContent, RenderMode } from '../types';
import { BlockContentContainer } from '../components/block/content-container';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import * as Yup from 'yup';
import { NativeFormDropdownOption, NativeFormElement, NativeFormTextInput } from './store';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { NeueListbox } from 'src/common/form/NeueListbox';
import { FormikProps } from 'formik';
import { NeueCheckboxes } from '../components/neue-checkboxes';
import { NeueJourneyButton } from '../components/neue-button/journey';
import { apiPostSubmitNativeForm } from 'src/utils/journeyApi';
import { usePlayerStore } from '../neue-player/player-store';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { promiseDelay } from 'src/common/helpers/promise-delay';
import { Nullable } from 'src/types/nullable.type';

type Props = {
  block: Block;
  selected: boolean;
  grabbing: boolean;
  editable: boolean;
  loading: boolean;
  contextMenuOpen: boolean;
  renderMode: RenderMode;
  onDelete: () => void;
  measureElementRef: (element: Nullable<HTMLElement>) => void;
};

export const NativeFormBlock = ({
  block,
  renderMode,
  onDelete,
  selected,
  grabbing,
  loading,
  contextMenuOpen,
  measureElementRef,
  editable,
}: Props) => {
  const blockContent = block.content as NativeFormBlockContent;
  const aliasData = usePlayerStore((state) => state.journeyData);
  const [isFormSent, setIsFormSent] = React.useState(false);
  const [formBeingSent, setFormBeingSent] = React.useState(false);

  const requiredFields = blockContent.fields.filter((field) => field.required);

  const validationSchema = Yup.object({
    ...requiredFields.reduce((acc, field) => {
      return {
        ...acc,
        [field.id]: Yup.string().required('Required'),
      };
    }, {}),
  });

  const getNativeFormSubmitter = (aliasUUID: string) => (values: object) => apiPostSubmitNativeForm(aliasUUID, values);

  const submitNativeForm = getNativeFormSubmitter(aliasData.alias_uuid);

  const formikParams = {
    initialValues: blockContent.fields.reduce((acc, field) => {
      const formField = field as Exclude<NativeFormElement, NativeFormTextInput>;
      return {
        ...acc,
        [field.id]: formField.value || '',
      };
    }, {}),
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values: Record<string, string>) => {
      setFormBeingSent(true);
      await promiseDelay(3_000);

      let payload: Record<string, object> = {};
      const valueEntries = Object.entries(values);
      for (const [key, value] of valueEntries) {
        const field = blockContent.fields.find((field) => field.id === key);
        if (!field) {
          return;
        }
        payload[field.id] = { label: field.label, value, type: field.type };
      }
      setIsFormSent(false);
      await submitNativeForm(payload);
      setFormBeingSent(false);
      setIsFormSent(true);
    },
  };

  const showHeader = blockContent.config && (blockContent.config.title || blockContent.config.description);
  const submitButtonAlignment = blockContent.config?.submitButtonAlignment || 'left';

  const isRenderModeEditor = renderMode === 'editor';
  const isLoadingSnipperManual = !isRenderModeEditor;

  return (
    <BlockContentContainer
      backgroundStyle='clear'
      loading={loading}
      selected={selected}
      grabbing={grabbing}
      contextMenuOpen={contextMenuOpen}
      renderMode={renderMode}
    >
      <div
        className={classNames('flex items-center transition-all inset-0 rounded-2xl z-10', {
          'hover:bg-neue-canvas-fg-10': isRenderModeEditor && !selected,
        })}
      >
        <div className='absolute inset-0 w-full flex items-center p-2 space-x-6'>
          <JourneyFormik {...formikParams}>
            {({ setFieldValue, errors, touched, isValid }: FormikProps<Record<string, string>>) => {
              return (
                <JourneyForm
                  className='w-[calc(100%-60px)] flex flex-col gap-4 shrink-0 justify-evenly'
                  ref={measureElementRef}
                >
                  {showHeader && (
                    <div className='flex flex-col gap-2'>
                      {blockContent.config.title && (
                        <h1 className='text-neue-canvas-desktop-title text-neue-canvas-fg'>
                          {blockContent.config.title}
                        </h1>
                      )}
                      {blockContent.config.description && (
                        <h2 className='text-neue-canvas-desktop-heading !font-medium text-neue-canvas-fg-50'>
                          {blockContent.config.description}
                        </h2>
                      )}
                    </div>
                  )}
                  <div className='flex flex-col gap-8 shrink-0 py-4'>
                    {blockContent.fields.map((field) => {
                      switch (field.type) {
                        case 'dropdown':
                          return (
                            <div className='flex flex-col gap-4 text-neue-canvas-fg shrink-0'>
                              <div className='flex flex-col gap-2'>
                                {field.label && (
                                  <div className='flex items-center gap-1'>
                                    <span className='text-neue-journey-medium-strong'>{field.label}</span>
                                    {field.required && <span className='text-bedrock-red'>*</span>}
                                  </div>
                                )}
                                {field.description && <p className='text-neue-journey-medium'>{field.description}</p>}
                                <NeueListbox
                                  defaultLabel='Select an option'
                                  options={field.options}
                                  renderKey={(option: NativeFormDropdownOption) => option.value}
                                  renderValue={(option: NativeFormDropdownOption) => option.label}
                                  labelClassName='text-neue-canvas-fg text-neue-journey-medium-strong'
                                  iconClassName='text-neue-journey-bg'
                                  buttonColorClassName='bg-neue-canvas-fg-5'
                                  onChange={(selected: NativeFormDropdownOption) => {
                                    setFieldValue(field.id, selected.label);
                                  }}
                                />
                                {errors[field.id] && touched[field.id] && (
                                  <p className='text-bedrock-p-small text-bedrock-red'>{errors[field.id]}</p>
                                )}
                              </div>
                            </div>
                          );
                        case 'input':
                          return (
                            <div className='flex flex-col gap-4 text-neue-canvas-fg shrink-0'>
                              <FormikTextInput
                                isNeue
                                showRequiredAsteriks={field.required}
                                labelClassName='text-neue-canvas-fg text-neue-journey-medium-strong'
                                inputClasses={classNames('w-full !border-neue-canvas-fg-20 !bg-neue-canvas-fg-5', {
                                  'pointer-events-none': renderMode === 'editor',
                                })}
                                id={field.id}
                                name={field.id}
                                required={field.required}
                                type='text'
                                label={field.label}
                                renderDescription={() => {
                                  return (
                                    <>
                                      {field.description && (
                                        <p className='text-neue-journey-medium mb-sm'>{field.description}</p>
                                      )}
                                    </>
                                  );
                                }}
                              />
                            </div>
                          );
                        case 'checkbox':
                          return (
                            <div className='flex flex-col gap-4 text-neue-canvas-fg shrink-0'>
                              <div className='flex flex-col gap-2'>
                                {field.label && (
                                  <div className='flex items-center gap-1'>
                                    <span className='text-neue-journey-medium-strong'>{field.label}</span>
                                    {field.required && <span className='text-bedrock-red'>*</span>}
                                  </div>
                                )}
                                {field.description && <p className='text-neue-journey-medium'>{field.description}</p>}
                                <NeueCheckboxes
                                  renderMode={renderMode}
                                  options={field.options}
                                  orientation={field.orientation}
                                  labelClassName='text-neue-canvas-fg text-neue-journey-medium-strong'
                                  value={field.value}
                                  onChange={(values) => {
                                    const valuesToSend = values.map((value) => {
                                      const option = field.options.find((option) => option.value === value);
                                      return option ? option.label : value;
                                    });
                                    setFieldValue(field.id, valuesToSend);
                                  }}
                                />
                                {errors[field.id] && touched[field.id] && (
                                  <p className='text-bedrock-p-small text-bedrock-red'>{errors[field.id]}</p>
                                )}
                              </div>
                            </div>
                          );
                        default:
                          return null;
                      }
                    })}
                    <div
                      className={classNames('flex items-center gap-2', {
                        'justify-start': submitButtonAlignment === 'left',
                        'justify-center': submitButtonAlignment === 'center',
                        'justify-end': submitButtonAlignment === 'right',
                      })}
                    >
                      <NeueJourneyButton
                        type='submit'
                        containerClassName='flex gap-2'
                        className='flex items-center justify-between h-10'
                        disabled={blockContent.fields.length === 0 || !isValid || formBeingSent}
                      >
                        {blockContent.config?.submitButtonText || 'Submit'}
                      </NeueJourneyButton>
                      {isLoadingSnipperManual && formBeingSent ? <LoadingSpinner containerClassName='w-5 h-5' /> : null}
                      {isFormSent && !formBeingSent && (
                        <span className='text-neue-canvas-fg'>Form has been submitted</span>
                      )}
                    </div>
                  </div>
                </JourneyForm>
              );
            }}
          </JourneyFormik>
        </div>
      </div>
      <div
        className={classNames('absolute inset-0 rounded-2xl transition bg-transparent z-20', {
          hidden: !editable || selected,
          'pointer-events-none': selected,
        })}
      />
    </BlockContentContainer>
  );
};
